import { type Dictionary, formatCommaNumber } from '@tmap-web-lib/utils'

export const getSchemeJsonData = (search: string): Dictionary<string> | null => {
  if (!search) {
    return null
  }

  return getQueryString(search.split('?')[1] || '')
}

export const getQueryString = (queryString: string) => {
  if (queryString === '') return null
  return queryString.split('&').reduce((map: { [index: string]: string }, part) => {
    const record = part.split('=')
    map[decodeURIComponent(record[0])] = decodeURIComponent(record[1] || '')
    return map as Dictionary<string>
  }, {})
}

export const getDecodeJsonObject = (str: string): Record<string, any> => {
  try {
    return JSON.parse(str.length ? decodeURIComponent(str) : '{}')
  }
  catch {
    return {}
  }
}

export const getEncodeJsonObject = (obj: Record<string, any>): string => {
  return encodeURIComponent(JSON.stringify(obj))
}

// 고객센터 스킴의 경우 redirect 되고, 나머지 서비스는 새로운 웹뷰로 열게 파싱
// FAQ 상세 화면 등에 사용
export const parseLinkUrlToBridgeUrl = ({ url }: { url: string }) => {
  return (
    url
      // 1. "&amp;"를 "&"로 변환(URL에 해당 값이 들어가는 경우를 방지)
      .replace(/&amp;/g, '&')
      // 2. 브릿지 페이지 스킴링크를 인앱에서 바로 띄우기 위해 기존 링크를 비활성화하고 openBridgeLink 함수를 호출한다.
      // FIXME: double quote와 single quote 두 가지 케이스를 모두 대응하고자 정규식 replace를 두 번 실행하고있음.
      // (?:"|') 와 같은 조건으로 잡을 시 정상적인 값으로 replace되지 않는 문제가 존재...
      .replace(
        /href="https:\/\/www.tmap.co.kr\/tmap2\/mobile\/tmap.jsp\?scheme=(.+?)&host=(.+?)">/g,
        'draggable="false" href="$1://$2" onclick="openBridgeLink(this);return false;" >',
      )
      .replace(
        /href='https:\/\/www.tmap.co.kr\/tmap2\/mobile\/tmap.jsp\?scheme=(.+?)&host=(.+?)'>/g,
        'draggable=\'false\' href=\'$1://$2\' onclick=\'openBridgeLink(this);return false;\'>',
      )
      // 3. 외부 브라우저로 띄우기 위해 기존 링크 동작을 비활성화하고 openBrowserLink 함수를 호출한다.
      .replace(
        /href="((http|https).+?)">/g,
        'draggable="false" href="$1" onclick="openBrowserLink(this);return false;" >',
      )
      .replace(
        /href='((http|https).+?)'>/g,
        'draggable=\'false\' href=\'$1\' onclick=\'openBrowserLink(this);return false;\'>',
      )
  )
}

// 무조건 새로운 웹뷰로 열게 파싱
// 공지사항 팝업에서만 사용
export const parseNoticePopupLink = ({ url }: { url: string }) => {
  return (
    url
      // 1. "&amp;"를 "&"로 변환(URL에 해당 값이 들어가는 경우를 방지)
      .replace(/&amp;/g, '&')
      // 2. 스킴링크를 인앱에서 바로 띄우기 위해 기존 링크를 비활성화하고 openSchemeLink 함수를 호출한다.
      // FIXME: double quote와 single quote 두 가지 케이스를 모두 대응하고자 정규식 replace를 두 번 실행하고있음.
      // (?:"|') 와 같은 조건으로 잡을 시 정상적인 값으로 replace되지 않는 문제가 존재...
      .replace(
        /href="tmap:\/\/(.+?)">/g,
        'draggable="false" href="tmap://$1" onclick="openSchemeLink(this,true);return false;" >',
      )
      .replace(
        /href='tmap:\/\/(.+?)'>/g,
        'draggable=\'false\' href=\'tmap://$1\' onclick=\'openSchemeLink(this,true);return false;\'>',
      )
      // 3. 브릿지 페이지 스킴링크를 인앱에서 바로 띄우기 위해 기존 링크를 비활성화하고 openBridgeLink 함수를 호출한다.
      // FIXME: double quote와 single quote 두 가지 케이스를 모두 대응하고자 정규식 replace를 두 번 실행하고있음.
      // (?:"|') 와 같은 조건으로 잡을 시 정상적인 값으로 replace되지 않는 문제가 존재...
      .replace(
        /href="https:\/\/www.tmap.co.kr\/tmap2\/mobile\/tmap.jsp\?scheme=(.+?)&host=(.+?)">/g,
        'draggable="false" href="$1://$2" onclick="openBridgeLink(this,true);return false;" >',
      )
      .replace(
        /href='https:\/\/www.tmap.co.kr\/tmap2\/mobile\/tmap.jsp\?scheme=(.+?)&host=(.+?)'>/g,
        'draggable=\'false\' href=\'$1://$2\' onclick=\'openBridgeLink(this,true);return false;\'>',
      )
      // 4. 외부 브라우저로 띄우기 위해 기존 링크 동작을 비활성화하고 openBrowserLink 함수를 호출한다.
      .replace(
        /href="((http|https).+?)">/g,
        'draggable="false" href="$1" onclick="openBrowserLink(this);return false;" >',
      )
      .replace(
        /href='((http|https).+?)'>/g,
        'draggable=\'false\' href=\'$1\' onclick=\'openBrowserLink(this);return false;\'>',
      )
  )
}

export const parseBridgeToSchemeURL = (url: string) => {
  return url
    .replace(
      /https:\/\/www.tmap.co.kr\/tmap2\/mobile\/tmap.jsp\?scheme=(.+?)&host=(.+?)/g,
      '$1://$2',
    )
    .replace(/tmap:\/\/(.+?)&(.+?)/g, 'tmap://$1?$2')
}

export const isJsonString = (str: string) => {
  try {
    JSON.parse(str)
  }
  catch {
    return false
  }
  return true
}

export const getArticleLabelNameValue = (labelNames: string[] = [], query: string) => {
  const regExp = new RegExp(`^${query}:(.*)`, 'g')

  if (labelNames?.some(e => regExp.test(e))) {
    return labelNames
      .find(e => regExp.test(e))
      ?.replace(regExp, '$1')
      .trim()
  }

  return undefined
}

export function getReplacedTemplate(body: string, placeholders: Record<string, any>): string {
  //  {{...}} 다음과 같은 포맷을 치환
  return body.replace(/\{\{\s*([^{}]*)\s*\}\}/g, (_, target) => {
    const trimmedTarget = target.trim()

    // additional.key 치환
    if (trimmedTarget.startsWith('additional.')) {
      const [, key] = trimmedTarget.split('.')
      return placeholders[key] ?? ''
    }

    // 플레이스홀더 포맷은 맞나, prefix가 additional이 아닌경우
    return ''
  })
}

export function formatFloorKilometers(value?: number | null) {
  if (typeof value !== 'number') {
    return ''
  }
  else if (value >= 1000) {
    return `${formatCommaNumber(Number((Math.floor((value / 1000) * 10) / 10).toFixed(1)))}km`
  }
  else {
    return `${value.toFixed(0)}m`
  }
}
